import React, { useState, useEffect } from 'react';
import SharedCard   from './SharedCard';
import SharedSelect from './SharedSelect';

const MONTHS = [
  { value: '01', label: 'Enero'      }, { value: '02', label: 'Febrero'    },
  { value: '03', label: 'Marzo'      }, { value: '04', label: 'Abril'      },
  { value: '05', label: 'Mayo'       }, { value: '06', label: 'Junio'      },
  { value: '07', label: 'Julio'      }, { value: '08', label: 'Agosto'     },
  { value: '09', label: 'Septiembre' }, { value: '10', label: 'Octubre'    },
  { value: '11', label: 'Noviembre'  }, { value: '12', label: 'Diciembre'  },
];

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const PaymentsPanel = () => {
  const today        = new Date();
  const defaultMonth = String(today.getMonth() + 1).padStart(2, '0');
  const defaultYear  = String(today.getFullYear());

  const [students, setStudents]               = useState([]);
  const [payments, setPayments]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [selectedMonth, setSelectedMonth]     = useState(defaultMonth);
  const [selectedYear, setSelectedYear]       = useState(defaultYear);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [filterStatus, setFilterStatus]       = useState(''); // '' | 'paid' | 'pending'
  const [msg, setMsg]                         = useState(null); // { type, text }

  const years = Array.from({ length: 5 }, (_, i) =>
    String(today.getFullYear() - 2 + i)
  );

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3000);
  };

  const loadData = async () => {
    try {
      const [stuRes, payRes] = await Promise.all([
        fetch('/api/list_students.php',  { credentials: 'include' }),
        fetch('/api/list_payments.php',  { credentials: 'include' }),
      ]);
      const [stuData, payData] = await Promise.all([stuRes.json(), payRes.json()]);

      setStudents(Array.isArray(stuData) ? stuData : []);

      const norm = (Array.isArray(payData) ? payData : []).map(p => ({
        ...p,
        studentId: String(p.studentId ?? p.student_id),
        month:     String(p.month).padStart(2, '0'),
        year:      String(p.year),
        amount:    parseFloat(p.amount) || 0,
      }));
      setPayments(norm);
    } catch {
      setStudents([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getStudentName = id => {
    const s = students.find(x => String(x.id) === String(id));
    return s?.studentName ?? s?.name ?? '—';
  };

  const getPaymentRecord = studentId =>
    payments.find(p =>
      p.studentId === String(studentId) &&
      p.month     === selectedMonth &&
      p.year      === selectedYear
    );

  // Marcar pago manualmente
  const handleTogglePaid = async (student) => {
    const rec    = getPaymentRecord(student.id);
    const isPaid = rec?.status === 'paid';

    try {
      let res;
      if (rec) {
        // Ya existe → update_payment_status
        res = await fetch('/api/update_payment_status.php', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentId: rec.id,
            status: isPaid ? 'pending' : 'paid',
          }),
        });
      } else {
        // No existe → crear
        res = await fetch('/api/create_or_update_payment.php', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentId: student.id,
            month:     parseInt(selectedMonth),
            year:      parseInt(selectedYear),
            amount:    0,
            status:    'paid',
          }),
        });
      }
      const data = await res.json();
      if (data.success) {
        showMsg('success', isPaid ? 'Marcado como pendiente.' : 'Pago registrado.');
        loadData();
      } else {
        showMsg('error', data.message || 'Error al actualizar.');
      }
    } catch {
      showMsg('error', 'Error de conexión.');
    }
  };

  // Comprobante de pago
  const handlePrintReceipt = (student) => {
    const rec = getPaymentRecord(student.id);
    if (!rec || rec.status !== 'paid') return;

    const monthLabel = MONTHS.find(m => m.value === selectedMonth)?.label || selectedMonth;
    const paidAt = rec.paidAt
      ? new Date(rec.paidAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8"/>
<title>Comprobante de Pago</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', system-ui, sans-serif; background: #f8f8f8; display: flex; justify-content: center; padding: 40px 16px; }
  .card { background: white; width: 100%; max-width: 520px; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,.1); }
  .header { background: #961914; padding: 28px 32px; color: white; }
  .header h1 { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; }
  .header p { font-size: 13px; opacity: .8; margin-top: 4px; }
  .badge { display: inline-block; background: rgba(255,255,255,.2); border-radius: 999px; padding: 4px 12px; font-size: 12px; margin-top: 12px; }
  .body { padding: 28px 32px; }
  .row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #f3f4f6; }
  .row:last-child { border-bottom: none; }
  .label { font-size: 13px; color: #6b7280; }
  .value { font-size: 14px; font-weight: 600; color: #111827; text-align: right; }
  .total-row { background: #fef2f2; border-radius: 10px; padding: 16px; margin-top: 16px; display: flex; justify-content: space-between; align-items: center; }
  .total-label { font-size: 14px; font-weight: 600; color: #961914; }
  .total-value { font-size: 24px; font-weight: 700; color: #961914; }
  .footer { padding: 20px 32px; background: #f9fafb; border-top: 1px solid #f3f4f6; }
  .footer p { font-size: 11px; color: #9ca3af; line-height: 1.6; }
  .seal { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .seal-dot { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; }
  .seal-text { font-size: 12px; font-weight: 600; color: #22c55e; }
  .actions { padding: 20px 32px 28px; display: flex; gap: 10px; }
  @media print { .actions { display: none; } body { background: white; padding: 0; } .card { box-shadow: none; border-radius: 0; } }
  button { flex: 1; padding: 11px; border-radius: 10px; border: none; cursor: pointer; font-size: 14px; font-weight: 600; transition: opacity .15s; }
  button:hover { opacity: .85; }
  .btn-primary { background: #961914; color: white; }
  .btn-secondary { background: #f3f4f6; color: #374151; }
</style>
</head>
<body>
<div class="card">
  <div class="header">
    <h1>Comprobante de Pago</h1>
    <p>Academia de Danza</p>
    <span class="badge">Folio: PAY-${String(rec.id).padStart(6,'0')}</span>
  </div>
  <div class="body">
    <div class="seal">
      <div class="seal-dot"></div>
      <span class="seal-text">PAGO CONFIRMADO</span>
    </div>
    <div class="row">
      <span class="label">Estudiante</span>
      <span class="value">${getStudentName(student.id)}</span>
    </div>
    <div class="row">
      <span class="label">Período</span>
      <span class="value">${monthLabel} ${selectedYear}</span>
    </div>
    <div class="row">
      <span class="label">Fecha de pago</span>
      <span class="value">${paidAt}</span>
    </div>
    <div class="row">
      <span class="label">Concepto</span>
      <span class="value">Mensualidad</span>
    </div>
    <div class="total-row">
      <span class="total-label">Total pagado</span>
      <span class="total-value">${mxn(rec.amount)}</span>
    </div>
  </div>
  <div class="footer">
    <div class="seal" style="margin-bottom:6px">
      <div class="seal-dot" style="background:#961914"></div>
      <span class="seal-text" style="color:#961914">ACADEMIA DE DANZA</span>
    </div>
    <p>Este comprobante es un documento informativo del registro de pago en el sistema de gestión. Para factura fiscal, solicítela en recepción con sus datos y RFC.</p>
  </div>
  <div class="actions">
    <button class="btn-primary" onclick="window.print()">Imprimir / PDF</button>
    <button class="btn-secondary" onclick="window.close()">Cerrar</button>
  </div>
</div>
<script>setTimeout(()=>{ try{window.print()}catch(e){} },500)</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.open(); win.document.write(html); win.document.close(); }
    else alert('Tu navegador bloqueó la ventana. Permite ventanas emergentes para imprimir.');
  };

  // Estadísticas del mes
  const activeStudents = students.filter(s => (s.status || '').toLowerCase() === 'activo');
  const paidThisMonth  = activeStudents.filter(s => getPaymentRecord(s.id)?.status === 'paid');
  const pendingCount   = activeStudents.length - paidThisMonth.length;
  const totalCollected = paidThisMonth.reduce((sum, s) => sum + (getPaymentRecord(s.id)?.amount || 0), 0);

  // Lista filtrada
  const visibleStudents = activeStudents
    .filter(s => !selectedStudentId || String(s.id) === String(selectedStudentId))
    .filter(s => {
      if (!filterStatus) return true;
      const rec = getPaymentRecord(s.id);
      const status = rec?.status === 'paid' ? 'paid' : 'pending';
      return status === filterStatus;
    });

  const monthLabel = MONTHS.find(m => m.value === selectedMonth)?.label || selectedMonth;

  if (loading) return (
    <SharedCard title="Gestión de Pagos Mensuales">
      <div className="flex justify-center items-center py-16">
        <div className="w-8 h-8 border-4 border-[#961914] border-t-transparent rounded-full animate-spin" />
      </div>
    </SharedCard>
  );

  return (
    <SharedCard title="Gestión de Pagos Mensuales">

      {/* Mensaje de feedback */}
      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {msg.text}
        </div>
      )}

      {/* Filtros */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SharedSelect
          label="Mes"
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
          options={MONTHS}
        />
        <SharedSelect
          label="Año"
          value={selectedYear}
          onChange={e => setSelectedYear(e.target.value)}
          options={years.map(y => ({ value: y, label: y }))}
        />
        <SharedSelect
          label="Estudiante"
          value={selectedStudentId}
          onChange={e => setSelectedStudentId(e.target.value)}
          options={[
            { value: '', label: 'Todos' },
            ...activeStudents.map(s => ({ value: s.id, label: s.name || s.studentName }))
          ]}
        />
        <SharedSelect
          label="Estado"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          options={[
            { value: '',        label: 'Todos'     },
            { value: 'paid',    label: 'Pagados'   },
            { value: 'pending', label: 'Pendientes'},
          ]}
        />
      </div>

      {/* Resumen del mes */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">Pagados</p>
          <p className="text-3xl font-extrabold text-green-700 mt-1">{paidThisMonth.length}</p>
          <p className="text-xs text-green-500 mt-0.5">de {activeStudents.length} alumnos</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
          <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Pendientes</p>
          <p className="text-3xl font-extrabold text-orange-700 mt-1">{pendingCount}</p>
          <p className="text-xs text-orange-500 mt-0.5">{monthLabel} {selectedYear}</p>
        </div>
        <div className="bg-[#961914]/5 border border-[#961914]/20 rounded-xl p-4 text-center">
          <p className="text-xs text-[#961914] font-semibold uppercase tracking-wide">Recaudado</p>
          <p className="text-2xl font-extrabold text-[#961914] mt-1">{mxn(totalCollected)}</p>
          <p className="text-xs text-[#961914]/60 mt-0.5">este mes</p>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
              <th className="py-3 px-5 text-left">Estudiante</th>
              <th className="py-3 px-5 text-left">Monto</th>
              <th className="py-3 px-5 text-left">Fecha de pago</th>
              <th className="py-3 px-5 text-center">Estado</th>
              <th className="py-3 px-5 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleStudents.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-5 py-8 text-center text-gray-400 text-sm">
                  No hay estudiantes en este filtro.
                </td>
              </tr>
            ) : visibleStudents.map(student => {
              const rec    = getPaymentRecord(student.id);
              const isPaid = rec?.status === 'paid';
              const paidAt = rec?.paidAt
                ? new Date(rec.paidAt).toLocaleDateString('es-MX')
                : null;

              return (
                <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-5">
                    <div className="font-semibold text-gray-900 text-sm">{student.name || student.studentName}</div>
                    <div className="text-xs text-gray-400">{student.discipline || ''}</div>
                  </td>
                  <td className="py-3 px-5 text-sm font-medium text-gray-700">
                    {rec?.amount > 0 ? mxn(rec.amount) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-3 px-5 text-sm text-gray-500">
                    {paidAt || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-3 px-5 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                      isPaid
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isPaid ? 'bg-green-500' : 'bg-orange-400'}`} />
                      {isPaid ? 'Pagado' : 'Pendiente'}
                    </span>
                  </td>
                  <td className="py-3 px-5 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleTogglePaid(student)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          isPaid
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {isPaid ? 'Marcar pendiente' : 'Marcar pagado'}
                      </button>
                      {isPaid && (
                        <button
                          onClick={() => handlePrintReceipt(student)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#961914]/10 text-[#961914] hover:bg-[#961914]/20 transition-colors"
                        >
                          Comprobante
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {visibleStudents.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan="5" className="px-5 py-2 text-xs text-gray-400 text-right">
                  Mostrando {visibleStudents.length} de {activeStudents.length} alumnos activos
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </SharedCard>
  );
};

export default PaymentsPanel;